import Image from 'next/image'
import React, { Children, isValidElement } from 'react'
import { BLOCKS, INLINES } from '@contentful/rich-text-types'
import type { Node } from '@contentful/rich-text-types'
import type { IFaqAsset } from '@/types/contentful'

type AssetNode = Node & { data?: { target?: { sys?: { id?: string } } } }

export const createNoticeRichTextRenderOptions = (assets: IFaqAsset[]) => {
  const assetMap = new Map<string, { title?: string; url?: string }>()
  assets.forEach((asset) => assetMap.set(asset.id, { title: asset.title, url: asset.url }))

  return {
    renderNode: {
      [BLOCKS.UL_LIST]: (_node: Node, children: React.ReactNode) => (
        <ul className="list-disc list-outside pl-5 flex flex-col gap-1">
          {children}
        </ul>
      ),
      [BLOCKS.OL_LIST]: (_node: Node, children: React.ReactNode) => (
        <ol className="list-decimal list-outside pl-5 flex flex-col gap-1">
          {children}
        </ol>
      ),
      [BLOCKS.LIST_ITEM]: (_node: Node, children: React.ReactNode) => {
        // Contentful 리스트 아이템은 보통 <p>를 자식으로 갖고, 그 <p>가 block이라
        // marker가 "•"만 한 줄에 보이는 현상이 생길 수 있어 <p>를 <span>으로 펼칩니다.
        const normalized = Children.map(children, (child) => {
          if (isValidElement(child) && child.type === 'p') {
            return (
              <span className="font-pretendard-regular text-[15px] text-main-light-2 dark:text-main-dark-2">
                {child.props.children}
              </span>
            )
          }
          return child
        })

        return (
          <li className="font-pretendard-regular text-[15px] text-main-light-2 dark:text-main-dark-2">
            {normalized}
          </li>
        )
      },
      [BLOCKS.EMBEDDED_ASSET]: (node: AssetNode) => {
        const assetId: string | undefined = node?.data?.target?.sys?.id
        const asset = assetId ? assetMap.get(assetId) : undefined
        if (!asset?.url) return null
        return (
          <div className="rounded-[13px] bg-gray-100 flex items-center justify-center py-[50px]">
            <Image
              src={asset.url}
              alt={asset.title || 'image'}
              className="max-w-full rounded-md"
              loading="lazy"
              width={500}
              height={500}
            />
          </div>
        )
      },
      [BLOCKS.PARAGRAPH]: (_node: Node, children: React.ReactNode) => {
        // Contentful에 "-" / "--" 를 줄 단위로 넣어둔 경우 "-"만 한 줄에 찍히는 문제 방지
        const arr = Children.toArray(children)
        if (arr.length === 1 && typeof arr[0] === 'string') {
          const raw = arr[0]
          const cleaned = raw
            .split('\n')
            .filter((line) => {
              const t = line.trim()
              return !/^--?$/.test(t)
            })
            .join('\n')
            .trimEnd()

          if (!cleaned.trim()) return null
          return (
            <p
              className="font-pretendard-regular text-[15px] text-main-light-2 dark:text-main-dark-2"
              style={{ whiteSpace: 'pre-line' }}
            >
              {cleaned}
            </p>
          )
        }

        return (
          <p
            className="font-pretendard-regular text-[15px] text-main-light-2 dark:text-main-dark-2"
            style={{ whiteSpace: 'pre-line' }}
          >
            {children}
          </p>
        )
      },
      [INLINES.HYPERLINK]: (node: Node, children: React.ReactNode) => {
        const uri = (node as unknown as { data?: { uri?: string } })?.data?.uri as string
        if (!uri) return children
        return (
          <a href={uri} target="_blank" rel="noopener noreferrer" className="underline text-blue-600 dark:text-blue-400">
            {children}
          </a>
        )
      },
      [BLOCKS.HEADING_1]: (_node: Node, children: React.ReactNode) => (
        <h1 className="font-pretendard-semibold text-[32px] text-main-light-2 dark:text-main-dark-2 leading-tight">
          {children}
        </h1>
      ),
    },
  }
}


